import {
  GetThreadForListedCompanyDocument,
  GetThreadForListedCompanyQuery,
  SendContextMessageMutation,
  SendMessageInput,
  SendMessageMutation,
  SendMessageRequestingGlAccountDataMutation,
  SendStockAnalysisMessageMutation,
  SendStockAnalysisQuestionInput,
  useGetThreadForListedCompanyQuery,
  useSendContextMessageMutation,
  useSendMessageMutation,
  useSendMessageRequestingGlAccountDataMutation,
  useSendStockAnalysisMessageMutation
} from 'generated/graphql';

import { gqlCacheUtil } from 'modules/common';
import React, {
  useCallback,
  useMemo
} from 'react';

import update from 'immutability-helper';
import _ from 'lodash';
import { ChatMessagePane } from 'modules/components/layer35';



export function useSendMessage() {
  const [
    fnMutate,
    tuple
  ] = useSendMessageMutation({
  });



  const fnWrappedMutate = React.useCallback(
    async (input: SendMessageInput) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<
            GetThreadForListedCompanyQuery,
            SendMessageMutation
          >(GetThreadForListedCompanyDocument,
            (prevState, { mutationResult, queryVariables }) => {
              if (queryVariables.listedCompanyId === input.listedCompanyId) {
                return update(prevState, {
                  messages: prevState.messages
                    ? {
                      $push: mutationResult.data?.sendMessage ?? []
                    }
                    : {
                      $set: mutationResult.data?.sendMessage ?? []
                    }
                });
              }

              return prevState;
            })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export interface IData {
  ticker: string;
  companyName: string;
  activeMessages: ChatMessagePane.IMessage[];
  historicalMessages: ChatMessagePane.IMessage[];
}

export function useQuery(listedCompanyId: string) {
  const tuple = useGetThreadForListedCompanyQuery({
    variables: {
      listedCompanyId
    },
    skip: listedCompanyId === ''
  });

  return React.useMemo(
    () => {
      const messages = tuple.data?.messages?.map((msg): ChatMessagePane.IMessage => {
        const time = msg.createdTimestamp;
        if (!time.isValid) {
          console.log(time);
        }
        return {
          id: msg.id,
          isHistorical: msg.isHistorical,
          content: msg.content,
          createdTimestamp: msg.createdTimestamp,
          relevantData: msg.relevantData
            ? {
              ...msg.relevantData,
              // Duplicate values are being returned by the backend right now. This filters them out.
              values: _.uniqBy(msg.relevantData.values, (v) => `${v.year}-${v.period}-${v.value}-${v.label}`)
            }
            : null,
          senderId: msg.sender?.id ?? null
        };
      }) ?? [];

      const data: IData = {
        ticker: tuple.data?.listedCompany?.ticker ?? '',
        companyName: tuple.data?.listedCompany?.companyName ?? '',

        activeMessages: messages.filter((msg) => !msg.isHistorical),
        historicalMessages: messages.filter((msg) => msg.isHistorical)
      };
      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSendContextMessage() {
  const [
    fnMutate,
    tuple
  ] = useSendContextMessageMutation({
  });

  const fnWrappedMutate = useCallback(
    async (contextQuestionId: string, listedCompanyId: string) => {
      await fnMutate({
        variables: {
          contextQuestionId,
          listedCompanyId
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<
            GetThreadForListedCompanyQuery,
            SendContextMessageMutation
          >(GetThreadForListedCompanyDocument,
            (prevState, { mutationResult, queryVariables }) => {
              const newMessages = mutationResult.data?.sendContextQuestion;
              if (
                !newMessages // No messages returned
                || queryVariables.listedCompanyId !== listedCompanyId // Not the company we care about.
              ) {
                return prevState;
              }

              const hasMessages = !_.isNil(prevState.messages) && prevState.messages.length > 0;
              if (hasMessages) {
                return update(prevState, {
                  messages: {
                    $push: [...newMessages]
                  }
                });
              }

              return update(prevState, {
                messages: {
                  $set: [...newMessages]
                }
              });
            })
        }
      });
    },
    [fnMutate]
  );

  return useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useSendMessageRequestingGlAccountData(companyId: string) {
  const [
    sendMessage,
    sendMessageTuple
  ] = useSendMessageRequestingGlAccountDataMutation({
    updateQueries: {
      ...gqlCacheUtil.genUpdateQueryMap<
        GetThreadForListedCompanyQuery,
        SendMessageRequestingGlAccountDataMutation
      >(GetThreadForListedCompanyDocument,
        (prevState, { mutationResult, queryVariables }) => {
          if (queryVariables.listedCompanyId === companyId) {
            const messages = mutationResult.data?.sendMessageRequestingGlAccountData ?? [];

            return update(prevState, {
              messages: prevState.messages
                ? {
                  $push: messages
                }
                : {
                  $set: messages
                }
            });
          }

          return prevState;
        })
    }
  });

  return [
    sendMessage,
    sendMessageTuple
  ] as const;
}



export function useSendStockAnalysisMessage() {
  const [
    fnMutate,
    tuple
  ] = useSendStockAnalysisMessageMutation({
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SendStockAnalysisQuestionInput) => {
      await fnMutate({
        variables: input,
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<
            GetThreadForListedCompanyQuery,
            SendStockAnalysisMessageMutation
          >(GetThreadForListedCompanyDocument,
            (prevState, { mutationResult, queryVariables }) => {
              const newMessages = mutationResult.data?.sendStockAnalysisQuestion;
              if (!newMessages || queryVariables.listedCompanyId !== input.listedCompanyId) {
                return prevState;
              }

              const hasMessages = !_.isNil(prevState.messages) && prevState.messages.length > 0;
              if (hasMessages) {
                return update(prevState, {
                  messages: {
                    $push: [...newMessages]
                  }
                });
              }

              return update(prevState, {
                messages: {
                  $set: [...newMessages]
                }
              });
            })
        }
      });
    },
    [fnMutate]
  );

  return useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
